import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  TbCategory2,
  TbVideo,
  TbAugmentedReality2,
  TbPackage,
  TbBrain,
  TbAward,
} from 'react-icons/tb';
import {
  PlayCircle,
  Eye,
  Package,
  Brain,
  ArrowLeft,
  CheckCircle,
  Clock,
  Award,
  Play,
  RefreshCw
} from 'lucide-react';
import { 
  useCategory, 
  useUserActivities, 
  useHierarchicalProgress, 
  useLocalStorage, 
  useModal 
} from '../../hooks';
import { Card, LoadingSpinner, Badge, ProgressBar, Button, Modal } from '../../components/common';
import { ProductType } from '../../types';

const HEADER_PRESETS = [
  'bg-[linear-gradient(180deg,white_0%,rgba(26,113,255,0.10)_45%,rgba(15,23,42,0.04)_100%)]',
  'bg-[linear-gradient(180deg,white_0%,rgba(0,168,1,0.12)_45%,rgba(15,23,42,0.04)_100%)]',
  'bg-[linear-gradient(180deg,white_0%,rgba(53,193,215,0.14)_45%,rgba(15,23,42,0.04)_100%)]',
];

function pickHeaderPreset(key: string | undefined) {
  if (!key) return HEADER_PRESETS[0];
  let sum = 0;
  for (let i = 0; i < Math.min(6, key.length); i++) sum += key.charCodeAt(i);
  const idx = sum % 3;
  return HEADER_PRESETS[idx];
}

const CategoryPage: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const queryClient = useQueryClient();
  
  // Obtener datos de la categoría
  const { data: categoryData, isLoading: categoryLoading } = useCategory(categoryId!);
  
  // Obtener actividades del usuario para esta categoría específica
  const { data: activitiesData, isLoading: activitiesLoading } = useUserActivities({
    categoryId: categoryId!, // Asegurar que categoryId se pase correctamente
    limit: 100
  });
  
  const [hasSeenCategoryTutorial, setHasSeenCategoryTutorial] = useLocalStorage(
    `hasSeenCategoryTutorial_${categoryId}`,
    false
  );
  const { isOpen: tutorialOpen, openModal: openTutorial, closeModal: closeTutorial } = useModal();

  const category = categoryData?.category;
  const allActivities = activitiesData?.users || [];
  
  // FILTRAR EXPLÍCITAMENTE por categoryId en el frontend como backup
  const filteredActivities = allActivities.filter(activity => activity.categoryId === categoryId);

  // Usar useHierarchicalProgress con las actividades filtradas
  const progress = useHierarchicalProgress(
    category?.subcategories || [], 
    filteredActivities
  ); 

  // Invalidar caché cuando se monta el componente
  useEffect(() => {
    if (categoryId) {
      queryClient.invalidateQueries({ queryKey: ['category', categoryId] });
      queryClient.invalidateQueries({ queryKey: ['userActivities'] });
    }
  }, [categoryId, queryClient]);

  // Mostrar tutorial específico de la categoría si es la primera vez
  useEffect(() => {
    if (category && !hasSeenCategoryTutorial) {
      openTutorial();
    }
  }, [category, hasSeenCategoryTutorial, openTutorial]);

  const handleTutorialComplete = () => {
    closeTutorial();
    setHasSeenCategoryTutorial(true);
  };

  const handleRefreshProgress = () => {
    // Forzar recarga de datos
    queryClient.invalidateQueries({ queryKey: ['category', categoryId] });
    queryClient.invalidateQueries({ queryKey: ['userActivities'] });
    // También refrescar después de un pequeño delay
    setTimeout(() => {
      queryClient.refetchQueries({ queryKey: ['userActivities'] });
    }, 500);
  };

  const getCategoryIcon = (type: ProductType) => {
    switch (type) {
      case ProductType.BLANQUEADOR:
        return '/assets/icons/01_blanqueadores.png';
      case ProductType.LIMPIADORES:
        return '/assets/icons/02_limpiadores.png';
      case ProductType.LAVALOZAS:
        return '/assets/icons/03_lavalozas.png';
      case ProductType.DETERGENTES:
        return '/assets/icons/04_detergentes.png';
      case ProductType.DESMANCHADORES:
        return '/assets/icons/05_desmanchadores.png';
      case ProductType.BLANCOX:
        return '/assets/icons/06_blancoxPRO.png';
      case ProductType.ALIMENTOS:
        return '/assets/icons/07_alimentos.png';
      default:
        return '📦';
    }
  };

  const headerBg = pickHeaderPreset(categoryId);

  if (categoryLoading || activitiesLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
        <div className="ml-4 text-gray-600">
          <p>Cargando datos...</p>
          {categoryLoading && <p className="text-sm">• Categoría</p>}
          {activitiesLoading && <p className="text-sm">• Actividades</p>}
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-brinsa-azul mb-4">
          Categoría no encontrada
        </h2>
        <p className="text-sm text-gray-600 mb-4">CategoryId: {categoryId}</p>
        <Link to="/home">
          <Button variant="outline">
            Volver a categorías
          </Button>
        </Link>
      </div>
    );
  }

  // Usar los valores calculados del hook useHierarchicalProgress
  const activities_list = [
    {
      id: 'video',
      title: 'Videos',
      description: 'Contenido educativo en video sobre los productos',
      icon: PlayCircle,
      chipBg: 'bg-brinsa-rojo/10',
      chipIcon: 'text-brinsa-rojo',
      path: `/category/${categoryId}/video`,
      completed: progress.video
    },
    {
      id: 'ar',
      title: 'Realidad Aumentada',
      description: 'Experiencia interactiva en realidad aumentada',
      icon: Eye,
      chipBg: 'bg-brinsa-morado/10',
      chipIcon: 'text-brinsa-morado',
      path: `/category/${categoryId}/ar`,
      completed: progress.ar
    },
    {
      id: 'portfolio',
      title: 'Portafolio de Productos',
      description: 'Explora el catálogo jerárquico de productos disponibles',
      icon: Package,
      chipBg: 'bg-brinsa-celeste/15',
      chipIcon: 'text-brinsa-celeste',
      path: `/category/${categoryId}/portfolio`,
      completed: progress.portfolio
    },
    {
      id: 'test',
      title: 'Prueba de Conocimientos',
      description: 'Evalúa lo que has aprendido',
      icon: Brain,
      chipBg: 'bg-brinsa-verde/10',
      chipIcon: 'text-brinsa-verde',
      path: `/category/${categoryId}/test`,
      completed: progress.test
    }
  ] as const;

  const completedCount = progress.completedActivities;
  const totalActivities = progress.totalActivities;
  const progressPercentage = progress.overallProgress;

  return (
    <div className="space-y-6">
      {/* Tutorial Modal */}
      <CategoryTutorialModal
        isOpen={tutorialOpen}
        onComplete={handleTutorialComplete}
        categoryName={category.name}
      />

      {/* Navegación */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-between">
          <Link
            to="/home"
            className="inline-flex items-center text-brinsa-azulOscuro hover:text-brinsa-azul transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a categorías
          </Link>
          
          {/* <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshProgress}
            disabled={activitiesLoading}
            leftIcon={<RefreshCw className={`w-4 h-4 ${activitiesLoading ? 'animate-spin' : ''}`} />}
          >
            Actualizar progreso
          </Button> */}
        </div>
      </motion.div>

      {/* Header de la categoría */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Card className="overflow-hidden">
          <div className={`${headerBg} p-8 relative`}>
            {/* Ícono de la categoría */}
            {(() => {
              const icon = getCategoryIcon(category.type);
              return (
                <div className="absolute top-4 right-4">
                  {typeof icon === 'string' && icon.startsWith('/') ? (
                    <div className="w-20 h-20 rounded-full bg-brinsa-celeste/30 grid place-items-center ring-1 ring-white/50">
                      <img
                        src={icon}
                        alt={category.name}
                        className="w-20 h-20 object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-brinsa-celeste/30 grid place-items-center">
                      <span className="text-brinsa-azul text-2xl">{icon}</span>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="relative z-10">
              <h1 className="text-3xl font-bold text-brinsa-azul mb-1">{category.name}</h1>
              <p className="text-base text-brinsa-azulOscuro/90 mb-6">
                {category.description}
              </p>

              {/* Progreso */}
              <div className="bg-white/70 backdrop-blur rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-brinsa-azulOscuro">
                    Progreso del módulo
                    {activitiesLoading && <span className="ml-2 text-xs">(actualizando...)</span>}
                  </span>
                  <span className="text-sm text-brinsa-azulOscuro">
                    {completedCount}/{totalActivities} completadas
                  </span>
                </div>
                <ProgressBar
                  value={progressPercentage}
                  max={100}
                  color="secondary"
                  className="bg-brinsa-celeste/30"
                />
              </div>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Actividades */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        {activities_list.map((activity, index) => (
          <motion.div
            key={activity.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            whileHover={{ y: -5 }}
            className="group"
          >
            <Card className="h-full hover:shadow-lg transition-all duration-300">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 ${activity.chipBg} rounded-lg flex items-center justify-center`}>
                    <activity.icon className={`w-6 h-6 ${activity.chipIcon}`} />
                  </div>
                  {activity.completed && (
                    <Badge variant="success" size="sm">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Completado
                    </Badge>
                  )}
                </div>

                <h3 className="text-lg font-semibold text-brinsa-azul mb-2">
                  {activity.title}
                </h3>
                <p className="text-brinsa-azulOscuro/80 mb-4">
                  {activity.description}
                </p>

                <Link to={activity.path}>
                  <Button
                    className="w-full"
                    variant={activity.completed ? 'outline' : 'primary'}
                  >
                    {activity.completed ? 'Revisar' : 'Comenzar'}
                  </Button>
                </Link>
              </div>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {/* Panel de progreso y certificado */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <Card title="Tu Progreso">
          <div className="space-y-6">
            {/* Estadísticas */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-brinsa-celeste/10 rounded-lg border border-slate-200">
                <div className="text-2xl font-bold text-brinsa-azul mb-1">
                  {completedCount}
                </div>
                <div className="text-sm text-brinsa-azulOscuro/80">Actividades completadas</div>
              </div>

              <div className="text-center p-4 bg-brinsa-verde/10 rounded-lg border border-slate-200">
                <div className="text-2xl font-bold text-brinsa-verde mb-1">
                  {Math.round(progressPercentage)}%
                </div>
                <div className="text-sm text-brinsa-azulOscuro/80">Progreso total</div>
              </div>

              <div className="text-center p-4 bg-brinsa-amarillo/10 rounded-lg border border-slate-200">
                <div className="text-2xl font-bold text-brinsa-amarillo mb-1">
                  {progress.totalTimeSpent ? Math.round(progress.totalTimeSpent / 60) : 0}
                </div>
                <div className="text-sm text-brinsa-azulOscuro/80">Minutos invertidos</div>
              </div>
            </div>

            {/* Progreso visual */}
            <div>
              <ProgressBar
                value={progressPercentage}
                max={100}
                showLabel
                color="primary"
              />
            </div>

            {/* Estado del certificado */}
            {completedCount === totalActivities ? (
              <div className="bg-brinsa-verde/10 border border-brinsa-verde/30 rounded-lg p-6 text-center">
                <Award className="w-12 h-12 text-brinsa-verde mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-brinsa-azul mb-2">
                  ¡Felicitaciones!
                </h3>
                <p className="text-brinsa-azulOscuro/85 mb-4">
                  Has completado todas las actividades del módulo {category.name}.
                  Tu certificado está listo para descargar.
                </p>
                <Link to="/certificates">
                  <Button variant="secondary">
                    Ver Certificados
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="bg-brinsa-celeste/10 border border-brinsa-celeste/30 rounded-lg p-6 text-center">
                <Clock className="w-12 h-12 text-brinsa-celeste mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-brinsa-azul mb-2">
                  Continúa aprendiendo
                </h3>
                <p className="text-brinsa-azulOscuro/85 mb-4">
                  Completa todas las actividades para obtener tu certificado del módulo {category.name}.
                </p>
                <p className="text-sm text-brinsa-azulOscuro">
                  Te faltan {totalActivities - completedCount} actividades por completar.
                </p>
              </div>
            )}
          </div>
        </Card>
      </motion.div>

      {/* Botón de ayuda */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="text-center"
      >
        <Button
          variant="outline"
          onClick={openTutorial}
          leftIcon={<Play className="w-4 h-4" />}
        >
          Ver tutorial de la categoría
        </Button>
      </motion.div>
    </div>
  );
};

// Modal de tutorial específico de categoría
interface CategoryTutorialModalProps {
  isOpen: boolean;
  onComplete: () => void;
  categoryName: string;
}

const CategoryTutorialModal: React.FC<CategoryTutorialModalProps> = ({
  isOpen,
  onComplete,
  categoryName
}) => {
  const [currentStep, setCurrentStep] = useState(0);

  const tutorialSteps = [
    {
      title: `Bienvenido al módulo de ${categoryName}`,
      description:
        'En este módulo encontrarás cuatro tipos diferentes de actividades para aprender sobre nuestros productos.',
      icon: <TbCategory2 className="w-12 h-12 text-brinsa-azul" />,
    },
    {
      title: 'Videos Educativos',
      description:
        'Mira videos informativos sobre los productos de esta categoría. El tiempo que dediques se registrará automáticamente.',
      icon: <TbVideo className="w-12 h-12 text-brinsa-rojo" />,
    },
    {
      title: 'Realidad Aumentada',
      description:
        'Experimenta con modelos 3D de los productos usando tu cámara. Una experiencia inmersiva e interactiva.',
      icon: <TbAugmentedReality2 className="w-12 h-12 text-brinsa-morado" />,
    },
    {
      title: 'Portafolio de Productos',
      description:
        'Explora todos los productos disponibles navegando por una estructura jerárquica organizada por presentaciones y tipos.',
      icon: <TbPackage className="w-12 h-12 text-brinsa-celeste" />,
    },
    {
      title: 'Prueba de Conocimientos',
      description:
        'Evalúa lo que has aprendido con pruebas interactivas tipo drag & drop y otras actividades dinámicas.',
      icon: <TbBrain className="w-12 h-12 text-brinsa-verde" />,
    },
    {
      title: 'Obtén tu Certificado',
      description:
        'Completa todas las actividades para desbloquear tu certificado de finalización del módulo.',
      icon: <TbAward className="w-12 h-12 text-brinsa-amarillo" />,
    },
  ];

  const nextStep = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
      setCurrentStep(0);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onComplete} size="lg">
      <div className="text-center p-6">
        <div className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-2xl bg-slate-100">
          {tutorialSteps[currentStep].icon}
        </div>
        <h3 className="text-xl font-bold text-brinsa-azul mb-4">
          {tutorialSteps[currentStep].title}
        </h3>
        <p className="text-brinsa-azulOscuro/85 mb-8">
          {tutorialSteps[currentStep].description}
        </p>

        {/* Indicadores de progreso */}
        <div className="flex justify-center space-x-2 mb-8">
          {tutorialSteps.map((_, index) => (
            <div
              key={index}
              className={`w-3 h-3 rounded-full ${
                index === currentStep ? 'bg-brinsa-azul' : 'bg-slate-300'
              }`}
            />
          ))}
        </div>

        {/* Botones de navegación */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 0}
          >
            Anterior
          </Button>
          <Button onClick={nextStep}>
            {currentStep === tutorialSteps.length - 1 ? 'Comenzar' : 'Siguiente'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CategoryPage;